import { useEffect, useState } from "react";
import api, { apiError, rows } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserMinus, UserCheck, ShieldCheck } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import Autocomplete from "@/components/panel/Autocomplete";
import { Badge, Button, DataTable, Field, Input, PageHeader, SearchInput, Select } from "@/components/ui-kit";

const ROLE_LABEL = { admin: "Administrador", vendedor: "Vendedor", balcao: "Balcão" };

/** Telas que o perfil de fato mostra no painel dele (ver ProfileController::index). */
const telas = (p) => p.screens_count ?? p.permissions?.length ?? 0;

/** Cadastro e edição usam o mesmo formulário; `user` preenchido = edição. */
function UserForm({ user, profiles, onClose, onSaved }) {
  const [form, setForm] = useState(user
    // Senha em branco na edição significa "manter a atual".
    ? { name: user.name, email: user.email, password: "", profile_id: user.profile?.id || "", meta_mensal: user.meta_mensal ?? 10000, commission_rate: user.commission_rate ?? 5 }
    : { name: "", email: "", password: "", profile_id: "", meta_mensal: 10000, commission_rate: 5 });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const profile = profiles.find((p) => p.id === form.profile_id);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    if (!form.email.trim()) { toast.error("Informe o e-mail"); return; }
    if (!form.profile_id) { toast.error("Escolha o perfil de acesso"); return; }
    if (!user && !form.password) { toast.error("Defina uma senha de acesso"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        meta_mensal: parseFloat(form.meta_mensal) || 0,
        commission_rate: parseFloat(form.commission_rate) || 0,
      };
      if (user && !payload.password) delete payload.password;

      if (user) await api.patch(`/admin/users/${user.id}`, payload);
      else await api.post("/admin/users", payload);

      toast.success(user ? "Usuário atualizado" : "Usuário cadastrado");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={user ? `Editar ${user.name}` : "Novo usuário"} maxWidth="max-w-md" testId="user-form-dialog">
      <div className="space-y-3">
        <Input value={form.name} onChange={set("name")} placeholder="Nome" data-testid="user-form-name" />
        <Input type="email" value={form.email} onChange={set("email")} placeholder="E-mail (usado para entrar)" data-testid="user-form-email" />
        <Input type="password" value={form.password} onChange={set("password")} data-testid="user-form-password" placeholder={user ? "Nova senha (deixe em branco para manter)" : "Senha"} />

        <Field
          label="Perfil de acesso"
          required
          /* O perfil define o papel: não é campo digitado. Como a dica depende
             do perfil escolhido, ela entra como `hint` do próprio campo — assim
             o leitor de tela a associa via aria-describedby. */
          /* `screens_count`, não `permissions.length`: fora do admin o painel
             entrega um subconjunto fixo, e a marcação podia prometer telas que
             o menu daquele papel não tem. Aqui o número é o que a pessoa vai
             ver ao entrar. */
          hint={profile
            ? `Entra como ${ROLE_LABEL[profile.base_role]} e enxerga ${telas(profile)} tela(s).`
            : undefined}
        >
          <Autocomplete
            value={form.profile_id}
            onChange={(v) => setForm({ ...form, profile_id: v })}
            options={profiles.map((p) => ({ value: p.id, label: `${p.name} · ${telas(p)} telas` }))}
            placeholder="Escolha o perfil"
            emptyLabel="— Nenhum perfil"
            testId="user-form-profile"
            width="w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input type="number" value={form.meta_mensal} onChange={set("meta_mensal")}
              aria-label="Meta mensal em reais" mono data-testid="user-form-meta" />
            <span className="block text-[10px] text-slate-400 mt-0.5">Meta mensal (R$)</span>
          </div>
          <div>
            <Input type="number" step="0.5" value={form.commission_rate} onChange={set("commission_rate")}
              aria-label="Comissão em porcentagem" mono data-testid="user-form-rate" />
            <span className="block text-[10px] text-slate-400 mt-0.5">Comissão (%)</span>
          </div>
        </div>

        <Button onClick={save} loading={saving} size="lg" block data-testid="user-form-save">
          {saving ? "Salvando..." : (user ? "Salvar alterações" : "Cadastrar")}
        </Button>
      </div>
    </Modal>
  );
}

export default function Users() {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [situacao, setSituacao] = useState("ativos");
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/admin/profiles").then((r) => setProfiles(rows(r))).catch(() => {});
  }, []);

  const {
    items: users, total, page, lastPage, setPage, search, setSearch,
    isLoading, isError, refetch: load,
  } = usePaginatedList("/admin/users", { situacao });

  // Desativar virou PATCH: o DELETE agora apaga de verdade.
  const deactivate = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: false }); toast.success(`${u.name} desativado`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setDeactivating(null); }
  };

  const reactivate = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: true }); toast.success(`${u.name} reativado`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success(`${u.name} excluído`); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setRemoving(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <PageHeader
        eyebrow="Sistema"
        title="Usuários"
        icon={ShieldCheck}
        description="Quem tem acesso ao sistema e com qual perfil. Clientes da loja ficam em Clientes (CRM)."
        actions={(
          <>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome ou e-mail" data-testid="users-search" aria-label="Buscar usuário por nome ou e-mail" width="w-52" />
            <Select value={situacao} onChange={(e) => setSituacao(e.target.value)} data-testid="users-situacao-filter"
              aria-label="Filtrar por situação" className="w-auto px-2.5 text-slate-600">
              <option value="ativos">Ativos</option>
              <option value="inativos">Desativados</option>
              <option value="todos">Todos</option>
            </Select>
            <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }} data-testid="users-new-btn">Novo usuário</Button>
          </>
        )}
      />

      <DataTable
        caption="Usuários com acesso ao sistema"
        rows={users}
        rowKey={(u) => u.id}
        loading={isLoading}
        error={isError}
        onRetry={load}
        rowClassName={(u) => (u.active ? undefined : "bg-slate-50/60")}
        empty={{
          icon: UserCheck,
          title: "Nenhum usuário encontrado",
          desc: situacao === "inativos"
            ? "Ninguém foi desativado até agora."
            : "Cadastre quem vai usar o sistema e escolha o perfil de acesso de cada um.",
          action: situacao !== "inativos" && <Button icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>Cadastrar usuário</Button>,
          testId: "users-empty",
        }}
        columns={[
          {
            key: "usuario", header: "Usuário",
            cell: (u) => (
              <>
                <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                  {u.name}
                  {!u.active && <Badge>Desativado</Badge>}
                </p>
                <p className="text-xs text-slate-400">{u.email}</p>
              </>
            ),
          },
          {
            key: "perfil", header: "Perfil de acesso",
            cell: (u) => (
              <Badge tone="info"><ShieldCheck size={12} aria-hidden /> {u.profile?.name || "Sem perfil"}</Badge>
            ),
          },
          { key: "entra", header: "Entra como", className: "text-slate-600", cell: (u) => ROLE_LABEL[u.role] || u.role },
          {
            key: "acoes", header: "Ações", align: "center", className: "whitespace-nowrap",
            cell: (u) => (
              <>
                <Button variant="ghost" size="iconSm" onClick={() => { setEditing(u); setShowForm(true); }}
                  data-testid="user-edit-btn" aria-label={`Editar ${u.name}`} className="hover:text-brand-600"><Pencil size={15} /></Button>
                {u.active
                  ? <Button variant="ghost" size="iconSm" onClick={() => setDeactivating(u)} data-testid="user-deactivate-btn"
                      aria-label={`Desativar ${u.name}`} title="Desativar (perde o acesso, histórico mantido)"
                      className="hover:text-red-500"><UserMinus size={15} /></Button>
                  : <Button variant="ghost" size="iconSm" onClick={() => reactivate(u)} data-testid="user-reactivate-btn"
                      aria-label={`Reativar ${u.name}`} className="hover:text-emerald-600"><UserCheck size={15} /></Button>}
                {/* Excluir só aparece para quem não tem histórico. Mostrar o
                    botão sempre seria oferecer uma ação que o banco recusa —
                    venda, caixa e nota apontam para o usuário. */}
                {u.deletable && (
                  <Button variant="ghost" size="iconSm" onClick={() => setRemoving(u)} data-testid="user-delete-btn"
                    aria-label={`Excluir ${u.name}`} title="Excluir de vez (só sem histórico)"
                    className="hover:text-red-600"><Trash2 size={15} /></Button>
                )}
              </>
            ),
          },
        ]}
      />
      <Pagination page={page} lastPage={lastPage} total={total} onChange={setPage} label="usuários" />

      {showForm && (
        <UserForm user={editing} profiles={profiles} onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }} />
      )}

      <ConfirmDialog
        open={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={() => deactivate(deactivating)}
        title="Desativar usuário"
        message={deactivating ? `Desativar "${deactivating.name}"? Ele perde o acesso imediatamente, mas vendas, comissões e caixas já registrados são mantidos. Você pode reativá-lo depois.` : ""}
        confirmLabel="Desativar"
      />

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title="Excluir usuário"
        message={removing
          ? `Excluir "${removing.name}" (${removing.email}) para sempre? Este usuário não tem venda, caixa nem nota no histórico, então nada de financeiro se perde — mas a exclusão não tem volta. Para quem saiu da empresa, prefira desativar.`
          : ""}
        confirmLabel="Excluir"
      />
    </div>
  );
}
